import { useState } from 'react';
import { type PolicyFeedbackAnnotationScope, type RiskLevel } from '@prisma/client';

// Define the schema for localStorage values
export type LocalStorageSchema = {
  sidebarExpanded: boolean;
  feedbackFilters: {
    riskTypes: string[];
    riskLevels: RiskLevel[];
    isNew: boolean;
  };
  currentWorkspaceId: string | null;
  /** Last ignore scope a reviewer submitted in Teach Your AI. */
  ignoreFeedbackScope: PolicyFeedbackAnnotationScope | null;
};

// Create strongly typed keys based on the schema
export const localStorageKeys: {
  [K in keyof LocalStorageSchema]: K;
} = {
  sidebarExpanded: 'sidebarExpanded',
  feedbackFilters: 'feedbackFilters',
  currentWorkspaceId: 'currentWorkspaceId',
  ignoreFeedbackScope: 'ignoreFeedbackScope',
} as const;

const setWorkspaceCookie = (workspaceId: string | null) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (!workspaceId) {
    document.cookie = 'currentWorkspaceId=; path=/; max-age=0; SameSite=Lax';
    return;
  }

  document.cookie = `currentWorkspaceId=${encodeURIComponent(workspaceId)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
};

/**
 * Type-safe useLocalStorage hook
 * @param key The key to store in localStorage (must be defined in schema)
 * @param initialValue The initial value if nothing is stored
 */
export function useLocalStorage<K extends keyof LocalStorageSchema>(
  key: K,
  initialValue: LocalStorageSchema[K],
): [
  LocalStorageSchema[K],
  (value: LocalStorageSchema[K] | ((val: LocalStorageSchema[K]) => LocalStorageSchema[K])) => void,
] {
  // Initialize state with value from localStorage or initialValue
  const [storedValue, setStoredValue] = useState<LocalStorageSchema[K]>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as LocalStorageSchema[K]) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key \"${key}\"`, error);
      return initialValue;
    }
  });

  // Custom setter function that updates both state and localStorage
  const setValue = (
    value: LocalStorageSchema[K] | ((val: LocalStorageSchema[K]) => LocalStorageSchema[K]),
  ) => {
    try {
      // Handle both direct value and functional updates
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      // Update React state
      setStoredValue(valueToStore);

      // Update localStorage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));

        if (key === localStorageKeys.currentWorkspaceId) {
          setWorkspaceCookie(valueToStore as string | null);
        }
      }
    } catch (error) {
      console.warn(`Error setting localStorage key \"${key}\"`, error);
    }
  };

  return [storedValue, setValue];
}
